import React, { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  formatInrAmount as formatCurrency,
  formatIndianNumber as formatNumber,
  formatIndianPercent as formatPercent,
  formatCompactInrAxis as formatCompact,
  formatLitres,
} from "../../../utils/reportFormat";

const EXPENSE_COLORS = [
  "#5D38DB",
  "#2563EB",
  "#16A34A",
  "#D97706",
  "#DC2626",
  "#7C3AED",
  "#0891B2",
  "#64748B",
];

const resolveSeriesColor = (index) => EXPENSE_COLORS[index % EXPENSE_COLORS.length];

const ExpenseTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) {
    return null;
  }

  const rows = [...payload]
    .filter((item) => item.value != null && item.value !== "")
    .sort((a, b) => (b.value || 0) - (a.value || 0));

  return (
    <div className="owner-chart-tooltip">
      <p className="owner-chart-tooltip__title">{label}</p>
      {rows.map((item) => (
        <div className="owner-chart-tooltip__row" key={item.dataKey}>
          <span
            className="owner-chart-tooltip__dot"
            style={{ backgroundColor: item.color }}
          />
          <span className="owner-chart-tooltip__label">{item.name}</span>
          <span className="owner-chart-tooltip__value">
            {formatCurrency(item.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

const SubItemTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) {
    return null;
  }

  const item = payload[0]?.payload;
  if (!item) {
    return null;
  }

  return (
    <div className="owner-chart-tooltip">
      <p className="owner-chart-tooltip__title">{item.label}</p>
      <div className="owner-chart-tooltip__row">
        <span className="owner-chart-tooltip__label">Amount</span>
        <span className="owner-chart-tooltip__value">
          {formatCurrency(item.amount)}
        </span>
      </div>
      <small className="text-muted">{formatPercent(item.pct)} of category</small>
    </div>
  );
};

const PieTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) {
    return null;
  }

  const item = payload[0];
  return (
    <div className="owner-chart-tooltip">
      <div className="owner-chart-tooltip__row">
        <span
          className="owner-chart-tooltip__dot"
          style={{ backgroundColor: item.payload.fill }}
        />
        <span className="owner-chart-tooltip__label">{item.name}</span>
        <span className="owner-chart-tooltip__value">
          {formatCurrency(item.value)}
        </span>
      </div>
      <small className="text-muted">{formatPercent(item.payload.pct)}</small>
    </div>
  );
};

const LegendContent = ({ payload }) => {
  if (!payload?.length) {
    return null;
  }

  return (
    <div className="owner-chart-legend">
      {payload.map((entry) => (
        <span className="owner-chart-legend__item" key={entry.value}>
          <span
            className="owner-chart-legend__swatch"
            style={{ backgroundColor: entry.color }}
          />
          {entry.value}
        </span>
      ))}
    </div>
  );
};

const OwnerExpenseBreakdownChart = ({
  data = [],
  series = [],
  summary = {},
  loading = false,
}) => {
  const categories = summary?.by_category || [];
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    if (!categories.length) {
      setSelectedCategory(null);
      return;
    }

    const stillExists = categories.some(
      (item) => item.category === selectedCategory
    );

    if (!selectedCategory || !stillExists) {
      setSelectedCategory(
        summary?.top_category || categories[0]?.category || null
      );
    }
  }, [categories, selectedCategory, summary?.top_category]);

  const pieData = useMemo(
    () =>
      categories.slice(0, 6).map((item, index) => ({
        name: item.category,
        value: item.amount,
        pct: item.pct,
        fill: resolveSeriesColor(index),
      })),
    [categories]
  );

  const selectedCategoryData = useMemo(
    () => categories.find((item) => item.category === selectedCategory) || null,
    [categories, selectedCategory]
  );

  const subItemChartData = useMemo(() => {
    const items = selectedCategoryData?.items || [];
    return items.map((item, index) => ({
      ...item,
      name: item.label,
      fill: resolveSeriesColor(index),
    }));
  }, [selectedCategoryData]);

  const activePeriods = useMemo(() => {
    let count = 0;
    let periodTotal = 0;

    data.forEach((point) => {
      const total = series.reduce((sum, item) => {
        const value = Number(point[item.key]);
        return Number.isFinite(value) ? sum + value : sum;
      }, 0);

      if (total > 0) {
        count += 1;
        periodTotal += total;
      }
    });

    return {
      count,
      average: count > 0 ? periodTotal / count : 0,
    };
  }, [data, series]);

  if (loading) {
    return (
      <div className="owner-chart-empty">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  if (!data.length || !series.length) {
    return (
      <div className="owner-chart-empty">
        <i className="fa fa-bar-chart owner-chart-empty__icon" aria-hidden="true" />
        <p>No expense data for the selected period.</p>
      </div>
    );
  }

  return (
    <div className="owner-sales-chart">
      <div className="owner-sales-kpi row g-3 mb-4">
        <div className="col-sm-6 col-xl-3">
          <div className="owner-sales-kpi__card owner-sales-kpi__card--brand">
            <span className="owner-sales-kpi__label">Total expenses</span>
            <strong className="owner-sales-kpi__value">
              {formatCurrency(summary?.total)}
            </strong>
          </div>
        </div>
        <div className="col-sm-6 col-xl-3">
          <div className="owner-sales-kpi__card owner-sales-kpi__card--amber">
            <span className="owner-sales-kpi__label">Top category</span>
            <strong className="owner-sales-kpi__value">
              {summary?.top_category || "—"}
            </strong>
            <small className="owner-sales-kpi__hint">
              {formatCurrency(summary?.top_amount)}
            </small>
          </div>
        </div>
        <div className="col-sm-6 col-xl-3">
          <div className="owner-sales-kpi__card owner-sales-kpi__card--blue">
            <span className="owner-sales-kpi__label">Avg per period</span>
            <strong className="owner-sales-kpi__value">
              {formatCurrency(activePeriods.average)}
            </strong>
          </div>
        </div>
        <div className="col-sm-6 col-xl-3">
          <div className="owner-sales-kpi__card owner-sales-kpi__card--green">
            <span className="owner-sales-kpi__label">Active periods</span>
            <strong className="owner-sales-kpi__value">{activePeriods.count}</strong>
          </div>
        </div>
      </div>

      <div className="row g-3">
        <div className="col-lg-8">
          <div className="owner-chart-panel">
            <ResponsiveContainer width="100%" height={360}>
              <BarChart
                data={data}
                margin={{ top: 12, right: 12, left: 4, bottom: 8 }}
              >
                <CartesianGrid
                  stroke="#E8ECF1"
                  strokeDasharray="3 3"
                  vertical={false}
                />
                <XAxis
                  dataKey="name"
                  tick={{ fill: "#64748b", fontSize: 12 }}
                  axisLine={{ stroke: "#e2e8f0" }}
                  tickLine={false}
                  dy={8}
                />
                <YAxis
                  tickFormatter={formatCompact}
                  tick={{ fill: "#64748b", fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  width={72}
                />
                <Tooltip content={<ExpenseTooltip />} />
                <Legend content={<LegendContent />} />

                {series.map((item, index) => {
                  const color = resolveSeriesColor(index);
                  return (
                    <Bar
                      key={item.key}
                      dataKey={item.key}
                      name={item.label}
                      stackId="expenses"
                      fill={color}
                      radius={
                        index === series.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]
                      }
                    />
                  );
                })}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="col-lg-4">
          <div className="owner-chart-panel owner-chart-panel--compact h-100">
            <p className="owner-chart-panel__subtitle mb-2">Period split</p>
            <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={58}
                  outerRadius={96}
                  paddingAngle={2}
                >
                  {pieData.map((entry) => (
                    <Cell key={entry.name} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip content={<PieTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="owner-expense-pie-legend">
              {pieData.map((item) => (
                <button
                  type="button"
                  key={item.name}
                  className={`owner-expense-pie-legend__row owner-expense-pie-legend__row--button ${
                    selectedCategory === item.name
                      ? "owner-expense-pie-legend__row--active"
                      : ""
                  }`}
                  onClick={() => setSelectedCategory(item.name)}
                >
                  <span
                    className="owner-chart-legend__swatch"
                    style={{ backgroundColor: item.fill }}
                  />
                  <span className="owner-expense-pie-legend__label">
                    {item.name}
                  </span>
                  <span className="owner-expense-pie-legend__value">
                    {formatPercent(item.pct)}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="owner-expense-drilldown mt-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <div>
            <h6 className="text-primary mb-1">Category detail</h6>
            <p className="small text-muted mb-0">
              Select a category to view its line-item split.
            </p>
          </div>
          {selectedCategoryData ? (
            <div className="owner-expense-drilldown__total">
              <span className="owner-expense-drilldown__total-label">
                {selectedCategoryData.category}
              </span>
              <strong>{formatCurrency(selectedCategoryData.amount)}</strong>
            </div>
          ) : null}
        </div>

        <div className="owner-expense-category-tabs mb-3">
          {categories.map((item) => (
            <button
              key={item.category}
              type="button"
              className={`owner-expense-category-tabs__btn ${
                selectedCategory === item.category
                  ? "owner-expense-category-tabs__btn--active"
                  : ""
              }`}
              onClick={() => setSelectedCategory(item.category)}
            >
              {item.category}
              <small>{formatCurrency(item.amount)}</small>
            </button>
          ))}
        </div>

        {subItemChartData.length ? (
          <div className="row g-3">
            <div className="col-lg-7">
              <div className="owner-chart-panel">
                <ResponsiveContainer width="100%" height={Math.max(220, subItemChartData.length * 42)}>
                  <BarChart
                    data={subItemChartData}
                    layout="vertical"
                    margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
                  >
                    <CartesianGrid
                      stroke="#E8ECF1"
                      strokeDasharray="3 3"
                      horizontal={false}
                    />
                    <XAxis
                      type="number"
                      tickFormatter={formatCompact}
                      tick={{ fill: "#64748b", fontSize: 12 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="label"
                      width={160}
                      tick={{ fill: "#475569", fontSize: 12 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip content={<SubItemTooltip />} />
                    <Bar dataKey="amount" radius={[0, 4, 4, 0]}>
                      {subItemChartData.map((entry) => (
                        <Cell key={entry.label} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="col-lg-5">
              <div className="owner-expense-item-table">
                <div className="owner-expense-item-table__head">
                  <span>Line item</span>
                  <span>Amount</span>
                  <span>Share</span>
                </div>
                {subItemChartData.map((item) => (
                  <div className="owner-expense-item-table__row" key={item.label}>
                    <span className="owner-expense-item-table__label">
                      <span
                        className="owner-chart-legend__swatch"
                        style={{ backgroundColor: item.fill }}
                      />
                      {item.label}
                    </span>
                    <span className="owner-expense-item-table__amount">
                      {formatCurrency(item.amount)}
                    </span>
                    <span className="owner-expense-item-table__pct">
                      {formatPercent(item.pct)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="owner-chart-empty owner-chart-empty--compact">
            <p>No line-item split available for this category.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OwnerExpenseBreakdownChart;
